import React, { useEffect } from 'react';
import { Input, Layout, Select, Tooltip, Typography, Grid } from 'antd';
import Icon, { SearchOutlined } from '@ant-design/icons';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styles from './Header.module.less';
import { routes } from '../../utils/routerHelper';
import { Button } from '../Button/Button';
import { isAuthorized } from '../../utils/auth';
import { isMobile, isTablet } from '../../utils/browser';
import { withRouter } from 'react-router';
import SupportIcon from '../../assets/img/supportIcon.svg?react';
import { callCareClicked, searchIconClicked } from '../../utils/analytika/events';
import { getAnalytikaScreenName } from '../../utils/analytika/reporterAnalytika';
import { ANALYTIKA_EVENTS_V2, ANALYTIKA_SCREENS } from '../../utils/analytika/enums';
import i18next from '../../i18next';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { User } from '../../api/models/user';
import { LoadCompleteFor, UserGalileoVars, UserSettings } from '../../types/store/user';
import { City, Location as LocationType } from '../../api/models/locations';
import { TFunction } from 'i18next';
import { History } from 'history';
import { AnalytikaEventsAttrType } from '../../api/dtos/analytika';
import { MerchantConfig } from '../../api/models/configs';
import { AppDispatch, AppState } from '../../types/store';
import { getCarePhoneNumber } from '../../utils/componentHelpers';

const { useBreakpoint } = Grid;

const { Option } = Select;

const getMobilePageHeader = (screenName: ANALYTIKA_SCREENS) => {
  switch (screenName) {
    case ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY:
      return i18next.t('deliveries.pageTitle');
    case ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERIES:
      return i18next.t('activeDeliveriesPage.title');
    case ANALYTIKA_SCREENS.PROFILE:
      return i18next.t('profile.pageTitle');
    case ANALYTIKA_SCREENS.PAYMENTS:
      return i18next.t('payments.pageTitle');
    case ANALYTIKA_SCREENS.ADDRESSES:
      return i18next.t('addresses.addressesTitle');
    case ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERIES:
      return i18next.t('pastDeliveriesPage.title');
    default:
      return '';
  }
};

export type HeaderProps = {
  fetchUserDetails: () => Promise<User>;
  changeLanguage: (lng: string) => void;
  logoutUser: () => Promise<void>;
  userData: User;
  userSettings: UserSettings;
  loadCompleteFor: LoadCompleteFor;
  cities: City[];
  selectedPickupLocation: LocationType;
  location: Location;
  t: TFunction;
  history: History;
  publishAnalytikaEvent: (eventAttrs: AnalytikaEventsAttrType) => void;
  isOtpEnabled: boolean;
  fetchMerchantConfig: () => Promise<MerchantConfig>;
  userGalileoVars: UserGalileoVars;
};

const Header = ({
  fetchUserDetails,
  changeLanguage,
  userSettings,
  selectedPickupLocation,
  t,
  history,
  publishAnalytikaEvent,
  location
}: HeaderProps) => {
  useEffect(() => {
    if (isAuthorized()) {
      fetchUserDetails();
    }
  }, [fetchUserDetails]);

  const { md: isMdBreakpoint, sm: isSmBreakpoint } = useBreakpoint();

  const changeLanguageHandler = (lng: string) => {
    changeLanguage(lng);
  };

  const onSearchHandler = (data: string) => {
    const currentScreenName = getAnalytikaScreenName(location?.pathname);
    const searchString = encodeURIComponent(data);
    publishAnalytikaEvent(searchIconClicked(currentScreenName, { search_text: searchString }));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_SEARCH,
      event_version: 1,
      search_term: searchString
    });
    history.push(`${routes.search}?term=${searchString}`);
  };

  const isSmallDevice = isMobile() || isTablet();
  const currentScreenName = getAnalytikaScreenName(location?.pathname);

  const headerPageName = getMobilePageHeader(currentScreenName);

  const selectedCity = selectedPickupLocation?.city || '';
  const carePhoneNumber = getCarePhoneNumber(selectedCity);
  return (
    <Layout.Header className={styles.header}>
      <div className={styles.content}>
        <div className={styles.navigationBarContainer}>
          {!isMdBreakpoint && headerPageName && (
            <Typography.Title className={styles.mobilePageHeader} level={isSmBreakpoint ? 3 : 4}>
              {headerPageName}
            </Typography.Title>
          )}
        </div>
        <>
          <div className={styles.headerActionsContainer}>
            <div className={styles.headerActions}>
              <>
                {isSmallDevice ? (
                  <Button size={'large'} onClick={() => onSearchHandler('')} icon={<SearchOutlined />} />
                ) : (
                  <div data-testid={'searchContainer'}>
                    <Input.Search
                      size={'large'}
                      className={styles.search}
                      placeholder={t('search.placeholder')}
                      onSearch={onSearchHandler}
                    />
                  </div>
                )}
                {!isMobile() && (
                  <>
                    <div>
                      <Select
                        size={'large'}
                        data-testid={'languageSelect'}
                        value={userSettings ? userSettings.defaultLanguage : 'en'}
                        onChange={changeLanguageHandler}
                        className={styles.languageChanger}
                      >
                        <Option data-testid={'languageSelectItem'} value="en">
                          {t('general.en')}
                        </Option>
                        <Option data-testid={'languageSelectItem'} value="ar">
                          {t('general.ar')}
                        </Option>
                      </Select>
                    </div>
                    <div>
                      <Tooltip title={t('header.careNumber', { number: carePhoneNumber })} placement="bottom">
                        <Button
                          href={`tel:${carePhoneNumber}`}
                          onClick={() => {
                            publishAnalytikaEvent(callCareClicked(currentScreenName));
                            publishAV2Event({
                              event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_CALL_CARE,
                              event_version: 1
                            });
                          }}
                          className={styles.supportIcon}
                        >
                          <Icon component={() => <SupportIcon />} />
                        </Button>
                      </Tooltip>
                    </div>
                  </>
                )}
              </>
            </div>
          </div>
        </>
      </div>
    </Layout.Header>
  );
};

const mapStateToProps = ({
  user: {
    userData,
    userSettings,
    selectedPickupLocation,
    cities,
    loadCompleteFor,
    merchantConfig: { isOtpEnabled },
    userGalileoVars
  }
}: AppState) => ({
  userData,
  userSettings,
  selectedPickupLocation,
  cities,
  loadCompleteFor,
  isOtpEnabled,
  userGalileoVars
});

const mapDispatchToProps = ({
  user: { fetchUserDetails, changeLanguage, logoutUser, publishAnalytikaEvent, fetchMerchantConfig }
}: AppDispatch) => ({
  fetchUserDetails,
  changeLanguage,
  logoutUser,
  publishAnalytikaEvent,
  fetchMerchantConfig
});
//@ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(Header)));
